import React, { useMemo } from 'react';
import { formatNumEU, formatPct, signedNum } from '../dummyData';
import { HiddenValue } from '../ValueHideContext';
import { useFilter, SortOption } from '../FilterContext';
import { SubscriptionSummary } from '../../../../types/subscription';
import { Transaction } from '../../types';
import { getBotColor } from '../../ClusterItem/Charts/helpers';
import { useDispatch } from 'react-redux';
import { setActiveSubscriptionId } from '../../../../store/slices/subscription';
import { useNavigate } from 'react-router-dom';
import { formatBalance } from '../../../../helpers/NumberFormatter';
import AssetIcon from '../../../../components/AssetIcon';
import './index.css';

const EXCHANGE_DISPLAY: Record<string, string> = {
  BINANCE: 'Binance',
  KUCOIN: 'KuCoin',
  MEXC: 'MEXC',
  COINBASE: 'Coinbase',
};

function resolveTxType(rawType: string | undefined | null): {
  name: string;
  color: string;
} {
  if (!rawType) return { name: 'Core', color: '#ffffff' };
  if (rawType === 'TAKE_PROFIT' || rawType === 'takeProfit') {
    return { name: 'Take Profit', color: '#f5c542' };
  }
  if (rawType === 'STOP_LOSS' || rawType === 'stopLoss') {
    return { name: 'Stop Loss', color: '#4a90d9' };
  }
  return { name: 'Core', color: '#ffffff' };
}

// ── Leaderboard value based on global sort ─────────────────────────────────

function getLeaderboardValue(s: SubscriptionSummary, sort: SortOption): number {
  switch (sort) {
    case 'Total Stable Profit':
      return s.profits?.total.stable.absolute ?? 0;
    case 'Total Asset Profit':
      return (s.profits?.total.asset.absolute ?? 0) * s.latestPrice;
    case 'Current Stable Profit':
      return s.latestTransaction?.orderType === 'BUY'
        ? s.profits?.current.stable?.absolute ?? 0
        : 0;
    case 'Current Asset Profit':
      return s.latestTransaction?.orderType === 'SELL'
        ? (s.profits?.current.asset?.absolute ?? 0) * s.latestPrice
        : 0;
    case 'Non-Algo Diff':
      return s.profits?.noAlgorithmicProgressionProfit.absolute ?? 0;
    default:
      return s.totalBalanceInStable;
  }
}

function getLeaderboardPct(
  s: SubscriptionSummary,
  sort: SortOption,
  totalValue: number,
): number {
  switch (sort) {
    case 'Total Stable Profit':
      return s.profits?.total.stable.relative ?? 0;
    case 'Total Asset Profit':
      return s.profits?.total.asset.relative ?? 0;
    case 'Current Stable Profit':
      return s.profits?.current.stable?.relative ?? 0;
    case 'Current Asset Profit':
      return s.profits?.current.asset?.relative ?? 0;
    case 'Non-Algo Diff':
      return s.profits?.noAlgorithmicProgressionProfit.relative ?? 0;
    default:
      return totalValue !== 0 ? s.totalBalanceInStable / totalValue : 0;
  }
}

// ── P&L Leaderboard ─────────────────────────────────────────────────────────

interface LeaderboardProps {
  subscriptions: SubscriptionSummary[];
  totalValue: number;
}

const PnlLeaderboard: React.FC<LeaderboardProps> = ({
  subscriptions,
  totalValue,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sort, query } = useFilter();

  const leaders = useMemo(() => {
    const filtered = query
      ? subscriptions.filter(
          (s) =>
            s.baseAsset.toLowerCase().includes(query.toLowerCase()) ||
            s.quoteAsset.toLowerCase().includes(query.toLowerCase()) ||
            s.exchange.toLowerCase().includes(query.toLowerCase()),
        )
      : subscriptions;

    const mapped = filtered.map((s) => ({
      sub: s,
      sym: s.baseAsset,
      color: getBotColor(s.baseAsset),
      value: getLeaderboardValue(s, sort),
      pct: getLeaderboardPct(s, sort, totalValue),
    }));

    // For "Total Value" sort by absolute value; for everything else sort by
    // the percentage so the leaderboard reflects relative performance.
    const sortKey =
      sort === 'Total Value'
        ? (x: (typeof mapped)[number]) => Math.abs(x.value)
        : (x: (typeof mapped)[number]) => Math.abs(x.pct);

    return mapped.sort((a, b) => sortKey(b) - sortKey(a));
  }, [subscriptions, query, sort, totalValue]);

  const maxLead =
    sort === 'Total Value'
      ? Math.max(...leaders.map((l) => Math.abs(l.value)), 1)
      : Math.max(...leaders.map((l) => Math.abs(l.pct)), 0.0001);

  const handleClick = (subscriptionId: number) => {
    dispatch(setActiveSubscriptionId(subscriptionId));
    navigate('/sub');
  };

  return (
    <div className="statistics-card">
      <div className="statistics-card-header">
        <h3 className="statistics-card-title">P&amp;L Leaderboard</h3>
      </div>

      <div className="statistics-card-body no-scrollbar">
        {leaders.map((leader, i) => {
          const isTotalValue = sort === 'Total Value';
          const isPositive = leader.value >= 0;
          const barColor = isTotalValue
            ? leader.color
            : isPositive
            ? '#00b061'
            : '#f0162f';
          const barWidth = isTotalValue
            ? (Math.abs(leader.value) / maxLead) * 100
            : (Math.abs(leader.pct) / maxLead) * 100;
          const rank = String(i + 1).padStart(2, '0');
          const valueColor = isTotalValue
            ? '#fff'
            : isPositive
            ? '#00b061'
            : '#f0162f';
          const valueStr = isTotalValue
            ? `$${formatNumEU(leader.value)}`
            : signedNum(leader.value);

          return (
            <div
              key={leader.sub.id}
              className="leaderboard-row"
              onClick={() => handleClick(leader.sub.id)}
              style={{ cursor: 'pointer' }}
            >
              <span className="leaderboard-rank">{rank}</span>
              <div className="leaderboard-sym-cell">
                <AssetIcon
                  baseAsset={leader.sym}
                  width={18}
                  height={18}
                  style={{ marginBottom: 0 }}
                />
                <span className="leaderboard-sym">{leader.sym}</span>
              </div>
              <div className="leaderboard-bar-wrapper">
                <div
                  className="leaderboard-bar"
                  style={{ width: `${barWidth}%`, background: barColor }}
                />
              </div>
              <span
                className="leaderboard-amount"
                style={{ color: valueColor }}
              >
                <HiddenValue width={60}>{valueStr}</HiddenValue>
              </span>
              <span className="leaderboard-pct" style={{ color: valueColor }}>
                {isTotalValue
                  ? `${(leader.pct * 100).toFixed(2).replace('.', ',')}%`
                  : formatPct(leader.pct)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Recent Transactions ──────────────────────────────────────────────────────

interface TxRowProps {
  tx: Transaction;
}

const TxBalanceBar: React.FC<{
  stableBalance: number;
  totalBalance: number;
}> = ({ stableBalance, totalBalance }) => {
  const quoteRatio =
    totalBalance > 0
      ? Math.max(0, Math.min(1, stableBalance / totalBalance))
      : 0;
  const assetRatio = 1 - quoteRatio;
  return (
    <div className="tx-balance-bar">
      {quoteRatio > 0 && (
        <div
          className="tx-balance-quote"
          style={{ width: `${quoteRatio * 100}%` }}
        />
      )}
      {assetRatio > 0 && (
        <div
          className="tx-balance-asset"
          style={{ width: `${assetRatio * 100}%` }}
        />
      )}
    </div>
  );
};

const TxRow: React.FC<TxRowProps> = ({ tx }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profitPct = tx.profit ?? 0;
  const profitPositive = profitPct >= 0;

  const date = new Date(tx.transactionTimestamp);
  const dateStr = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
  const timeStr = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);

  const txType = resolveTxType(tx.transactionType);
  const exchangeName = EXCHANGE_DISPLAY[tx.exchange] || tx.exchange;

  const handleClick = () => {
    dispatch(setActiveSubscriptionId(tx.subscriptionId));
    navigate('/sub');
  };

  return (
    <div className="tx-row-wrapper">
      <div
        className="tx-row"
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      >
        <span
          className={`tx-badge ${
            tx.orderType === 'BUY' ? 'tx-badge--buy' : 'tx-badge--sell'
          }`}
        >
          {tx.orderType}
        </span>

        <div className="tx-pair-col">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <AssetIcon
              baseAsset={tx.baseAsset}
              width={18}
              height={18}
              style={{ marginBottom: 0 }}
            />
            <span className="tx-pair-name">
              {tx.baseAsset}/{tx.quoteAsset}
            </span>
          </div>
          <span className="tx-pair-sub">
            {exchangeName} &middot;{' '}
            <span style={{ color: txType.color }}>{txType.name}</span>
          </span>
        </div>

        <span
          className={`tx-profit ${
            profitPositive ? 'color-positive' : 'color-negative'
          }`}
        >
          {formatPct(profitPct)}
        </span>

        <span className="tx-price">
          <HiddenValue width={50}>${formatNumEU(tx.orderPrice)}</HiddenValue>
        </span>

        <span className="tx-amount">
          <HiddenValue>{tx.assetQuantity}</HiddenValue> {tx.baseAsset}
        </span>

        <div className="tx-balance-cell">
          <TxBalanceBar
            stableBalance={tx.currentStableBalance}
            totalBalance={tx.totalBalance}
          />
        </div>

        <span className="tx-sub-total">
          <HiddenValue width={50}>
            ${formatBalance(tx.totalBalance, true)}
          </HiddenValue>
        </span>

        <div className="tx-timestamp">
          <span className="tx-timestamp-date">{dateStr}</span>
          <span className="tx-timestamp-time">{timeStr}</span>
        </div>
      </div>
    </div>
  );
};

interface TransactionsProps {
  transactions: Transaction[];
}

const RecentTransactions: React.FC<TransactionsProps> = ({ transactions }) => {
  const { query } = useFilter();

  const filtered = useMemo(() => {
    if (!query) return transactions;
    const q = query.toLowerCase();
    return transactions.filter(
      (tx) =>
        tx.baseAsset.toLowerCase().includes(q) ||
        (tx.quoteAsset && tx.quoteAsset.toLowerCase().includes(q)) ||
        tx.exchange.toLowerCase().includes(q),
    );
  }, [transactions, query]);

  return (
    <div className="statistics-card">
      <div className="statistics-card-header">
        <h3 className="statistics-card-title">Recent Transactions</h3>
      </div>

      <div className="tx-header">
        <span>Type</span>
        <span>Pair</span>
        <span>Profit</span>
        <span>Price</span>
        <span>Amount</span>
        <span>Balance</span>
        <span>Total</span>
        <span>Time</span>
      </div>

      <div className="statistics-card-body no-scrollbar">
        {filtered.map((tx) => (
          <TxRow key={tx.id} tx={tx} />
        ))}
      </div>
    </div>
  );
};

// ── Statistics ───────────────────────────────────────────────────────────────

interface StatisticsProps {
  subscriptions: SubscriptionSummary[];
  transactions: Transaction[];
  totalValue: number;
}

const Statistics: React.FC<StatisticsProps> = ({
  subscriptions,
  transactions,
  totalValue,
}) => {
  return (
    <div className="statistics-container">
      <PnlLeaderboard subscriptions={subscriptions} totalValue={totalValue} />
      <RecentTransactions transactions={transactions} />
    </div>
  );
};

export default Statistics;
